import dayjs from 'dayjs'
import { computed, ref } from 'vue'
import mrStoreApi from '@/api/biz/mrStoreApi'
import mrStoreStaffApi from '@/api/biz/mrStoreStaffApi'
import mrAppCatalogApi from '@/api/biz/mrAppCatalogApi'
import { BUSINESS_STATUS_GROUPS, loadBusinessStatusContract, statusCodeByKey } from '@/utils/businessStatusContract'
import tool from '@/utils/tool'

export const STAFF_POSITION_DICT = 'STAFF_POSITION'
export const STAFF_POSITION_VALUES = {
	FRONT_DESK: 'FRONT_DESK',
	THERAPIST: 'THERAPIST',
	STORE_MANAGER: 'STORE_MANAGER',
	CASHIER: 'CASHIER',
	PRINCIPAL: 'PRINCIPAL',
	OWNER: 'OWNER'
}

// 可提供服务/可被预约：按最新业务，门店任何在职人员理论上都可上钟，岗位不再限制
export const SERVICEABLE_POSITION_VALUES = Object.values(STAFF_POSITION_VALUES)

export function isServicePosition(position) {
	// 解耦后：只要岗位非空即视为可服务，避免未来新增岗位被卡
	if (!position || !String(position).trim()) return false
	return true
}

export const roleOptions = ref(buildDictSelectOptions(STAFF_POSITION_DICT))

export const roleLabelMap = computed(() => toLabelMap(roleOptions.value))
export const roleColorMap = computed(() => toColorMap(roleOptions.value))

function refreshRoleOptions() {
	roleOptions.value = buildDictSelectOptions(STAFF_POSITION_DICT)
}

if (typeof window !== 'undefined') {
	window.addEventListener('digit:data:set', (event) => {
		if (event?.detail?.key === 'DICT_TYPE_TREE_DATA') {
			refreshRoleOptions()
		}
	})
}

export const activeStatusOptions = ref([])
export const activeStatusLabelMap = computed(() => toLabelMap(activeStatusOptions.value))
export const activeStatusColorMap = computed(() => toColorMap(activeStatusOptions.value))

export const compensationSettlementModeOptions = [
	{ label: '按比例提成', value: 'RATE' },
	{ label: '按单固定金额', value: 'FIXED_PER_ORDER' }
]

export const compensationSettlementModeLabelMap = toLabelMap(compensationSettlementModeOptions)

export const commissionSourceTypeOptions = [
	{ label: '服务项目', value: 'SERVICE_ORDER_ITEM' },
	{ label: '次卡套票销售', value: 'TICKET_PACKAGE_ORDER' },
	{ label: '会员充值销售', value: 'MEMBER_RECHARGE_ORDER' }
]

export const commissionSourceTypeLabelMap = toLabelMap(commissionSourceTypeOptions)

export const commissionRuleStatusOptions = activeStatusOptions
export const commissionRuleStatusLabelMap = activeStatusLabelMap

export const commissionSettlementStatusOptions = ref([])
export const commissionSettlementStatusLabelMap = computed(() => toLabelMap(commissionSettlementStatusOptions.value))
export const commissionSettlementStatusColorMap = computed(() => toColorMap(commissionSettlementStatusOptions.value))

export const staffStatusOptions = ref([])
export const staffStatusLabelMap = computed(() => toLabelMap(staffStatusOptions.value))
export const staffStatusColorMap = computed(() => toColorMap(staffStatusOptions.value))
export const payrollStatusOptions = ref([])
export const payrollStatusLabelMap = computed(() => toLabelMap(payrollStatusOptions.value))
export const payrollStatusColorMap = computed(() => toColorMap(payrollStatusOptions.value))
export const photoClockOptions = ref([])
export const photoClockLabelMap = computed(() => toLabelMap(photoClockOptions.value))

let staffStatusPromise = null

const contractOptions = (contract, group) =>
	contract[group].map((item) => ({
		label: item.label,
		value: item.code,
		color: item.color,
		key: item.key,
		enabled: item.enabled
	}))

export function ensureStaffStatusOptions() {
	if (!staffStatusPromise) {
		staffStatusPromise = loadBusinessStatusContract()
			.then((contract) => {
				activeStatusOptions.value = contractOptions(contract, BUSINESS_STATUS_GROUPS.ACTIVE)
				commissionSettlementStatusOptions.value = contractOptions(
					contract,
					BUSINESS_STATUS_GROUPS.STAFF_COMMISSION_SETTLEMENT
				)
				staffStatusOptions.value = contractOptions(contract, BUSINESS_STATUS_GROUPS.STAFF)
				payrollStatusOptions.value = contractOptions(contract, BUSINESS_STATUS_GROUPS.STAFF_PAYROLL)
				photoClockOptions.value = contractOptions(contract, BUSINESS_STATUS_GROUPS.ATTENDANCE_CONFIG)
				return contract
			})
			.catch((error) => {
				staffStatusPromise = null
				throw error
			})
	}
	return staffStatusPromise
}

export async function loadActiveStatusCode(key = 'ACTIVE') {
	const contract = await ensureStaffStatusOptions()
	return statusCodeByKey(contract, BUSINESS_STATUS_GROUPS.ACTIVE, key)
}

export const manualAdjustmentTypeOptions = [
	{ label: '手工奖金', value: 'MANUAL_BONUS' },
	{ label: '手工扣款', value: 'MANUAL_DEDUCTION' }
]

export const payrollLineTypeLabelMap = {
	BASE_SALARY: '底薪',
	COMMISSION: '提成',
	MANUAL_BONUS: '手工奖金',
	MANUAL_DEDUCTION: '手工扣款'
}

ensureStaffStatusOptions().catch((error) => console.error('业务状态契约加载失败', error))

let bmobileAppCatalogCache = []
let bmobileAppOptionsCache = []
let bmobileAppLabelMapCache = {}
let bmobileAppCatalogPromise = null
let bmobileAppCatalogRevision = 0
const BMOBILE_APP_CATALOG_CHANGE_EVENT = 'digit:bmobile-app-catalog-change'
let pcAppCatalogCache = []
let pcAppCatalogPromise = null
let pcAppCatalogRevision = 0
const PC_APP_CATALOG_CHANGE_EVENT = 'digit:pc-app-catalog-change'
let authChannelLabelMapCache = {}
let authChannelPromise = null

export function toLabelMap(options) {
	return options.reduce((result, item) => {
		result[item.value] = item.label
		return result
	}, {})
}

export function toColorMap(options) {
	return options.reduce((result, item) => {
		if (item.color) {
			result[item.value] = item.color
		}
		return result
	}, {})
}

export function buildDictSelectOptions(dictCode) {
	return (tool.dictTypeList(dictCode) || [])
		.map((item) => ({
			label: item.dictLabel || item.label || item.name || item.dictValue,
			value: item.dictValue,
			color: parseDictColor(item.extJson)
		}))
		.filter((item) => item.value)
}

function parseDictColor(extJson) {
	if (!extJson) {
		return ''
	}
	if (typeof extJson === 'object') {
		return extJson.color || ''
	}
	try {
		return JSON.parse(extJson)?.color || ''
	} catch (error) {
		return ''
	}
}

export function getStaffDisplayName(staff) {
	return staff?.stageName || staff?.name || staff?.staffName || '未命名员工'
}

export async function loadStaffOptions(extra = {}) {
	const list = await mrStoreStaffApi.mrStoreStaffList({ current: 1, size: 500, ...extra })
	return (list || []).map((staff) => ({
		label: getStaffDisplayName(staff),
		value: staff.id,
		raw: staff
	}))
}

let storeOptionsPromise = null

export function invalidateStoreOptions() {
	storeOptionsPromise = null
}

export async function loadStoreOptions(extra = {}) {
	// 无额外过滤条件时共享同一次请求结果，避免各页面重复拉取门店列表
	const useCache = Object.keys(extra).length === 0
	const fetchOptions = () =>
		mrStoreApi.storeList({ current: 1, size: 500, ...extra }).then((list) =>
			(list || []).map((store) => ({
				label: store.name || store.storeName || '未命名门店',
				value: store.id,
				raw: store
			}))
		)
	if (!useCache) {
		return fetchOptions()
	}
	if (!storeOptionsPromise) {
		storeOptionsPromise = fetchOptions().catch((error) => {
			// 失败不缓存，允许下次重试
			storeOptionsPromise = null
			throw error
		})
	}
	return storeOptionsPromise
}

function normalizeAuthChannelLabelMap(channels) {
	return (channels || []).reduce((result, item) => {
		if (!item?.id) {
			return result
		}
		result[item.id] = item.mpName || item.name || '-'
		return result
	}, {})
}

function normalizeBmobileCatalog(categories) {
	return (categories || [])
		.map((category) => {
			const apps = (category?.apps || [])
				.filter((app) => app?.appCode)
				.map((app) => ({
					...app,
					appCode: String(app.appCode || '')
						.trim()
						.toUpperCase(),
					appName: app.appName || '未命名应用',
					categoryName: category.categoryName || '未分类'
				}))
			return {
				...category,
				categoryName: category?.categoryName || '未分类',
				apps
			}
		})
		.filter((category) => category.apps.length > 0)
}

async function ensureBmobileAppCatalog(force = false) {
	if (!force && bmobileAppCatalogCache.length > 0) {
		return bmobileAppCatalogCache
	}
	if (!force && bmobileAppCatalogPromise) {
		return bmobileAppCatalogPromise
	}
	const revision = force ? ++bmobileAppCatalogRevision : bmobileAppCatalogRevision
	const request = mrAppCatalogApi
		.withApps({ clientType: 'BMOBILE' })
		.then((categories) => {
			const catalog = normalizeBmobileCatalog(categories)
			const options = []
			const labelMap = {}
			catalog.forEach((category) => {
				category.apps.forEach((app) => {
					const label = app.appName || '未命名应用'
					options.push({
						label,
						value: app.appCode,
						raw: app
					})
					labelMap[app.appCode] = label
				})
			})
			if (revision === bmobileAppCatalogRevision) {
				bmobileAppCatalogCache = catalog
				bmobileAppOptionsCache = options
				bmobileAppLabelMapCache = labelMap
			}
			return catalog
		})
		.finally(() => {
			if (revision === bmobileAppCatalogRevision) bmobileAppCatalogPromise = null
		})
	bmobileAppCatalogPromise = request
	return request
}

export async function loadBmobileAppCatalog(force = false) {
	return ensureBmobileAppCatalog(force)
}

export async function loadBmobileAppOptions(force = false) {
	await ensureBmobileAppCatalog(force)
	return bmobileAppOptionsCache
}

export async function loadBmobileAppLabelMap(force = false) {
	await ensureBmobileAppCatalog(force)
	return bmobileAppLabelMapCache
}

async function ensurePcAppCatalog(force = false) {
	if (!force && pcAppCatalogCache.length > 0) return pcAppCatalogCache
	if (!force && pcAppCatalogPromise) return pcAppCatalogPromise
	const revision = force ? ++pcAppCatalogRevision : pcAppCatalogRevision
	const request = mrAppCatalogApi
		.withApps({ clientType: 'PC' })
		.then((categories) => {
			const catalog = normalizeBmobileCatalog(categories)
			if (revision === pcAppCatalogRevision) pcAppCatalogCache = catalog
			return catalog
		})
		.finally(() => {
			if (revision === pcAppCatalogRevision) pcAppCatalogPromise = null
		})
	pcAppCatalogPromise = request
	return request
}

export async function loadPcAppCatalog(force = false) {
	return ensurePcAppCatalog(force)
}

export function invalidateBmobileAppCatalog() {
	bmobileAppCatalogRevision += 1
	bmobileAppCatalogCache = []
	bmobileAppOptionsCache = []
	bmobileAppLabelMapCache = {}
	bmobileAppCatalogPromise = null
	if (typeof window !== 'undefined') {
		window.dispatchEvent(new CustomEvent(BMOBILE_APP_CATALOG_CHANGE_EVENT))
	}
}

export function subscribeBmobileAppCatalogChange(handler) {
	if (typeof window === 'undefined') return () => {}
	window.addEventListener(BMOBILE_APP_CATALOG_CHANGE_EVENT, handler)
	return () => window.removeEventListener(BMOBILE_APP_CATALOG_CHANGE_EVENT, handler)
}

export function invalidatePcAppCatalog() {
	pcAppCatalogRevision += 1
	pcAppCatalogCache = []
	pcAppCatalogPromise = null
	if (typeof window !== 'undefined') {
		window.dispatchEvent(new CustomEvent(PC_APP_CATALOG_CHANGE_EVENT))
	}
}

export function subscribePcAppCatalogChange(handler) {
	if (typeof window === 'undefined') return () => {}
	window.addEventListener(PC_APP_CATALOG_CHANGE_EVENT, handler)
	return () => window.removeEventListener(PC_APP_CATALOG_CHANGE_EVENT, handler)
}

async function ensureAuthChannelLabelMap(force = false) {
	if (!force && Object.keys(authChannelLabelMapCache).length > 0) {
		return authChannelLabelMapCache
	}
	if (!force && authChannelPromise) {
		return authChannelPromise
	}
	authChannelPromise = mrStoreStaffApi
		.mrStoreStaffChannelOptions()
		.then((channels) => {
			authChannelLabelMapCache = normalizeAuthChannelLabelMap(channels)
			return authChannelLabelMapCache
		})
		.finally(() => {
			authChannelPromise = null
		})
	return authChannelPromise
}

export async function loadAuthChannelLabelMap(force = false) {
	await ensureAuthChannelLabelMap(force)
	return authChannelLabelMapCache
}

export function resolveAuthChannelName(labelMap, channelId) {
	if (!channelId) {
		return '-'
	}
	return labelMap?.[channelId] || '-'
}

export function resolveOption(options, value) {
	return (options || []).find((item) => String(item.value) === String(value))
}

export function resolveOptionLabel(options, value, fallback = '-') {
	return resolveOption(options, value)?.label || fallback
}

export function normalizeTags(values) {
	return [...new Set((values || []).map((item) => String(item || '').trim()).filter(Boolean))]
}

export function normalizeAppCodes(values) {
	return [
		...new Set(
			(values || [])
				.map((item) =>
					String(item || '')
						.trim()
						.toUpperCase()
				)
				.filter(Boolean)
		)
	]
}

export function parseJsonArray(value) {
	if (Array.isArray(value)) {
		return normalizeTags(value)
	}
	if (value === null || value === undefined || value === '') {
		return []
	}
	if (typeof value === 'string') {
		try {
			const parsed = JSON.parse(value)
			if (Array.isArray(parsed)) {
				return normalizeTags(parsed)
			}
		} catch (error) {
			return normalizeTags(value.split(/[\n,，]/))
		}
	}
	return []
}

export function stringifyJsonArray(values) {
	return JSON.stringify(normalizeTags(values))
}

export function resolveBmobileAppLabel(labelMap, appCode) {
	return labelMap?.[appCode] || '未匹配应用'
}

export function resolveBmobileAppLabels(appCodes, labelMap) {
	return normalizeAppCodes(appCodes).map((item) => resolveBmobileAppLabel(labelMap, item))
}

export function formatIdentifierPreview(value, options = {}) {
	const text = String(value || '').trim()
	if (!text) {
		return options.emptyText || '-'
	}
	const head = Number.isFinite(options.head) ? Math.max(1, options.head) : 6
	const tail = Number.isFinite(options.tail) ? Math.max(1, options.tail) : 6
	if (text.length <= head + tail + 1) {
		return text
	}
	return `${text.slice(0, head)}...${text.slice(-tail)}`
}

export async function copyText(text) {
	const value = String(text || '').trim()
	if (!value) {
		return false
	}
	try {
		if (navigator?.clipboard?.writeText) {
			await navigator.clipboard.writeText(value)
			return true
		}
	} catch (error) {
		// Fallback to execCommand when clipboard API is unavailable.
	}
	const textarea = document.createElement('textarea')
	textarea.value = value
	textarea.setAttribute('readonly', 'readonly')
	textarea.style.position = 'fixed'
	textarea.style.opacity = '0'
	document.body.appendChild(textarea)
	textarea.focus()
	textarea.select()
	let copied = false
	try {
		copied = document.execCommand('copy')
	} finally {
		document.body.removeChild(textarea)
	}
	return copied
}

export function formatDateTime(value) {
	const dateValue = value ? dayjs(value) : null
	return dateValue?.isValid() ? dateValue.format('YYYY-MM-DD HH:mm:ss') : '-'
}

export function formatDate(value) {
	const dateValue = value ? dayjs(value) : null
	return dateValue?.isValid() ? dateValue.format('YYYY-MM-DD') : '-'
}

export function formatMonth(value) {
	const dateValue = value ? dayjs(value) : null
	return dateValue?.isValid() ? dateValue.format('YYYY-MM') : '-'
}

export function formatMoney(value) {
	if (value === null || value === undefined || value === '') {
		return '-'
	}
	const amount = Number(value)
	return Number.isFinite(amount) ? amount.toFixed(2) : '-'
}

export function formatRate(value) {
	if (value === null || value === undefined || value === '') {
		return '-'
	}
	const rate = Number(value)
	return Number.isFinite(rate) ? `${(rate * 100).toFixed(2)}%` : '-'
}

export function formatCompensationValue(plan) {
	if (!plan) {
		return '-'
	}
	if (plan.settlementMode === 'FIXED_PER_ORDER') {
		return `${formatMoney(plan.fixedCommissionAmount)} / 单`
	}
	return formatRate(plan.defaultCommissionRate)
}

export function getHealthCertStatus(expireDate) {
	if (!expireDate) {
		return { label: '未维护', color: 'default', tone: 'empty', days: null }
	}
	const today = dayjs().startOf('day')
	const target = dayjs(expireDate).startOf('day')
	const diffDays = target.diff(today, 'day')
	if (diffDays < 0) {
		return { label: '已过期', color: 'error', tone: 'expired', days: diffDays }
	}
	if (diffDays <= 30) {
		return { label: `${diffDays} 天内到期`, color: 'warning', tone: 'expiring', days: diffDays }
	}
	return { label: '有效', color: 'success', tone: 'valid', days: diffDays }
}
